import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Key, Plus, Copy, Eye, EyeOff, Trash2, AlertCircle, Check, Shield
} from "lucide-react";

export default function MultiAPIKeys({ company, subscription, user, getAPI, fetchCompanyData, fetchData, handleTabChange }) {
  const [keys, setKeys] = useState([]);
  const [maxKeys, setMaxKeys] = useState(3);
  const [canCreate, setCanCreate] = useState(false);
  const [planId, setPlanId] = useState("");
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  const [newlyCreated, setNewlyCreated] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedReason, setBlockedReason] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${getAPI()}/subscriptions/api-keys`, { withCredentials: true });
      setKeys(res.data.keys || []);
      setMaxKeys(res.data.max_keys);
      setCanCreate(res.data.can_create);
      setPlanId(res.data.plan_id);
      setIsBlocked(res.data.is_blocked || false);
      setBlockedReason(res.data.blocked_reason);
    } catch (e) {
      console.error("Failed to fetch API keys:", e);
    } finally {
      setLoading(false);
    }
  }, [getAPI]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) { toast.error("Please enter a name for the key"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${getAPI()}/subscriptions/api-keys/create`, { name: newKeyName.trim() }, { withCredentials: true });
      toast.success(`Key "${newKeyName}" created`);
      setNewlyCreated({ key_id: res.data.key_id, api_key: res.data.api_key, name: res.data.name });
      setNewKeyName("");
      setShowCreate(false);
      await fetchKeys();
      await fetchCompanyData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId, keyName) => {
    if (!window.confirm(`Revoke "${keyName}"? Any integrations using this key will stop working.`)) return;
    try {
      await axios.delete(`${getAPI()}/subscriptions/api-keys/${keyId}`, { withCredentials: true });
      toast.success(`Key "${keyName}" revoked`);
      if (newlyCreated?.key_id === keyId) setNewlyCreated(null);
      await fetchKeys();
      await fetchCompanyData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke key");
    }
  };

  const copyKey = (key, identifier) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(identifier || key);
    toast.success("API key copied");
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const activeKeys = keys.filter(k => k.active);
  const revokedKeys = keys.filter(k => !k.active);
  const hasSubscription = subscription && ["active", "trial", "trialing"].includes(subscription.status);

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-violet-600 border-t-transparent" />
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Your API Keys</h3>
          <p className="text-sm text-gray-500">
            {activeKeys.length} of {maxKeys} keys used
            <span className="text-gray-400 ml-1">({planId} plan)</span>
          </p>
        </div>
        {hasSubscription && canCreate && !isBlocked && user?.email_verified && (
          <Button
            size="sm"
            onClick={() => setShowCreate(!showCreate)}
            className="bg-gradient-to-r from-violet-600 to-indigo-600"
            data-testid="create-api-key-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New Key
          </Button>
        )}
      </div>

      {/* Create new key form */}
      {showCreate && (
        <div className="mb-4 p-4 bg-violet-50 border border-violet-200 rounded-lg">
          <p className="text-sm font-medium text-gray-700 mb-2">Create a new API key</p>
          <div className="flex gap-2">
            <Input
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g. Production, Development, Staging"
              className="flex-1"
              maxLength={50}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              data-testid="new-key-name-input"
            />
            <Button onClick={handleCreate} disabled={creating || !newKeyName.trim()} size="sm" data-testid="confirm-create-key-btn">
              {creating ? "Creating..." : "Create"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setNewKeyName(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Newly created key banner */}
      {newlyCreated && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="new-key-banner">
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800">Key "{newlyCreated.name}" created. Copy it now — you won't see the full key again.</p>
              <div className="flex items-center gap-2 mt-2">
                <code className="text-xs font-mono bg-white border px-2 py-1 rounded text-green-900 select-all break-all flex-1">{newlyCreated.api_key}</code>
                <Button size="sm" variant="outline" onClick={() => copyKey(newlyCreated.api_key, "new")} className="flex-shrink-0">
                  {copiedKey === "new" ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <button onClick={() => setNewlyCreated(null)} className="text-green-400 hover:text-green-600 text-sm">Dismiss</button>
          </div>
        </div>
      )}

      {/* Blocked warning */}
      {isBlocked && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
          <div className="flex gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">API Keys Blocked</p>
              <p className="text-sm text-red-700">{blockedReason || "Your account has been restricted. All existing API keys are deactivated. Contact support for assistance."}</p>
            </div>
          </div>
        </div>
      )}

      {/* No subscription warning */}
      {!hasSubscription && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
          <div className="flex gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">No Active Subscription</p>
              <p className="text-sm text-red-700">
                You need an active subscription to create and use API keys.{" "}
                <button className="text-red-800 underline font-medium" onClick={() => handleTabChange("billing")}>Activate a plan</button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Active keys */}
      {activeKeys.length === 0 && hasSubscription ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <Key className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-3">{isBlocked ? "All API keys have been deactivated" : "No API keys yet"}</p>
          {!isBlocked && user?.email_verified ? (
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-violet-600 to-indigo-600">
              <Plus className="w-4 h-4 mr-1" /> Create Your First Key
            </Button>
          ) : !isBlocked ? (
            <p className="text-xs text-gray-400">Verify your email to create API keys</p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-2">
          {activeKeys.map((k) => (
            <div key={k.key_id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors group" data-testid={`api-key-row-${k.key_id}`}>
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                <Key className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{k.name}</span>
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Active</Badge>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <code className="text-xs font-mono text-gray-500">
                    {visibleKeys[k.key_id || k.key_full] ? k.key_full : k.key_preview}
                  </code>
                  <button onClick={() => setVisibleKeys(prev => ({ ...prev, [k.key_id || k.key_full]: !prev[k.key_id || k.key_full] }))} className="text-gray-400 hover:text-gray-600">
                    {visibleKeys[k.key_id || k.key_full] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  Created {k.created_at ? new Date(k.created_at).toLocaleDateString() : ""}
                  {k.created_by ? ` by ${k.created_by}` : ""}
                  {k.last_used_at ? ` \u00b7 Last used ${new Date(k.last_used_at).toLocaleDateString()}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => copyKey(k.key_full, k.key_id || k.key_full)} title={isBlocked ? "Keys blocked" : "Copy key"} className="h-8 w-8 p-0" disabled={isBlocked}>
                  {copiedKey === (k.key_id || k.key_full) ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleRevoke(k.key_id || k.key_full, k.name)} title={isBlocked ? "Keys blocked" : "Revoke key"} className="h-8 w-8 p-0 text-gray-400 hover:text-red-600" disabled={isBlocked}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Limit info */}
      {!canCreate && activeKeys.length > 0 && (
        <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Key limit reached for your {planId} plan ({maxKeys} keys).
          {planId !== "free_unlimited" && planId !== "custom" && planId !== "enterprise" && (
            <> <button className="text-violet-600 underline" onClick={() => handleTabChange("billing")}>Upgrade</button> for more.</>
          )}
        </p>
      )}

      {/* Revoked keys (collapsed) */}
      {revokedKeys.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
            {revokedKeys.length} revoked key{revokedKeys.length > 1 ? "s" : ""}
          </summary>
          <div className="space-y-1 mt-2">
            {revokedKeys.map((k) => (
              <div key={k.key_id} className="flex items-center gap-3 p-2 rounded-lg opacity-50">
                <Key className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500 line-through">{k.name || "Unnamed"}</span>
                <code className="text-xs font-mono text-gray-400">{k.key_preview}</code>
                <Badge variant="outline" className="text-xs text-red-400 border-red-200 ml-auto">Revoked</Badge>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Security note */}
      <div className="mt-4 p-3 bg-amber-50 rounded-lg">
        <div className="flex gap-2">
          <Shield className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-amber-800">Keep your API keys secure</p>
            <p className="text-xs text-amber-700">Never share keys in public repos or client-side code. All keys share your plan's usage limits.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
